import React, { useState, useEffect, useContext } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button, TextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, InputAdornment, Switch } from "@mui/material";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import EmailIcon from "@material-ui/icons/Email";
import LockIcon from "@material-ui/icons/Lock";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";

import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import fluyraLogoDark from "../../assets/fluyra-logo-dark.png";

const REMEMBERED_LOGIN_KEY = "rememberedLogin";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "grid",
    gridTemplateColumns: "minmax(320px, 1.05fr) minmax(420px, 0.95fr)",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, #050d11 0%, #0a1b22 55%, #112a36 100%)"
        : "linear-gradient(135deg, #f7f9fb 0%, #eef2f7 100%)",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  brandPanel: {
    position: "relative",
    overflow: "hidden",
    padding: theme.spacing(6, 6, 5),
    display: "flex",
    alignItems: "stretch",
    [theme.breakpoints.down("md")]: {
      minHeight: 320,
      padding: theme.spacing(4, 3, 2),
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: 260,
    },
  },
  brandGlow: {
    position: "absolute",
    inset: 0,
    background: "none",
    pointerEvents: "none",
  },
  brandCard: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    borderRadius: 28,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(241, 241, 241, 0.08)" : "rgba(10, 27, 34, 0.08)"}`,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(165deg, rgba(7, 16, 21, 0.98) 0%, rgba(5, 13, 17, 0.98) 100%)"
        : "linear-gradient(165deg, #112a36 0%, #0a1b22 52%, #050d11 100%)",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 30px 80px rgba(0, 0, 0, 0.32)"
        : "0 28px 72px rgba(15, 23, 42, 0.12)",
    padding: theme.spacing(5),
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3),
    },
  },
  brandContent: {
    position: "relative",
    flex: 1,
    minHeight: 640,
    background:
      theme.palette.type === "dark"
        ? "radial-gradient(circle at 82% 22%, rgba(59, 130, 246, 0.1), transparent 18%), linear-gradient(165deg, #112a36 0%, #0a1b22 52%, #050d11 100%)"
        : "radial-gradient(circle at 82% 22%, rgba(59, 130, 246, 0.08), transparent 18%), linear-gradient(165deg, #112a36 0%, #0a1b22 52%, #050d11 100%)",
    borderRadius: 20,
    overflow: "hidden",
    padding: theme.spacing(5),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    [theme.breakpoints.down("md")]: {
      minHeight: 320,
      padding: theme.spacing(3.5),
    },
  },
  brandGrid: {
    position: "absolute",
    inset: 0,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)"
        : "linear-gradient(rgba(241,241,241,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(241,241,241,0.03) 1px, transparent 1px)",
    backgroundSize: "42px 42px",
    maskImage: "linear-gradient(180deg, rgba(0,0,0,0.9), rgba(0,0,0,0.12))",
    pointerEvents: "none",
  },
  brandOrbit: {
    position: "absolute",
    borderRadius: "50%",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(241,241,241,0.08)"}`,
    pointerEvents: "none",
  },
  orbitOne: {
    width: 440,
    height: 440,
    top: -120,
    right: -80,
  },
  orbitTwo: {
    width: 620,
    height: 620,
    bottom: -260,
    left: -160,
  },
  brandHeader: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    maxWidth: 460,
  },
  brandLogo: {
    width: 220,
    maxWidth: "100%",
    height: "auto",
    filter:
      theme.palette.type === "dark"
        ? "drop-shadow(0 14px 34px rgba(0, 0, 0, 0.34))"
        : "none",
  },
  brandBadge: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    minHeight: 32,
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: theme.palette.primary.main,
    background: theme.fluyra.tokens.accentSoft,
    border: `1px solid ${theme.fluyra.tokens.accentSoft}`,
  },
  slogan: {
    fontFamily: '"Monument Extended", sans-serif',
    fontSize: 28,
    lineHeight: 1.18,
    letterSpacing: "-0.03em",
    color: "#f1f1f1",
    [theme.breakpoints.down("sm")]: {
      fontSize: 22,
    },
  },
  brandText: {
    maxWidth: 500,
    color: "rgba(241, 241, 241, 0.72)",
    lineHeight: 1.7,
    fontSize: 15,
  },
  featureList: {
    position: "relative",
    zIndex: 1,
    display: "grid",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(4),
  },
  featureItem: {
    display: "grid",
    gridTemplateColumns: "44px 1fr",
    gap: theme.spacing(1.5),
    alignItems: "center",
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 18,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(241,241,241,0.08)" : "rgba(10,27,34,0.08)"}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(5, 13, 17, 0.34)"
        : "rgba(5, 13, 17, 0.3)",
    backdropFilter: "blur(10px)",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 14px 36px rgba(0, 0, 0, 0.16)"
        : "0 14px 36px rgba(15, 23, 42, 0.08)",
  },
  featureIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 14,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.primary.main,
    background: theme.fluyra.tokens.accentSoft,
  },
  featureTitle: {
    fontSize: 17,
    fontWeight: 600,
    color: "#f1f1f1",
    lineHeight: 1.35,
  },
  metricsRow: {
    position: "relative",
    zIndex: 1,
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  metricCard: {
    padding: theme.spacing(1.75),
    borderRadius: 18,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(241,241,241,0.08)" : "rgba(10,27,34,0.08)"}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(17, 42, 54, 0.52)"
        : "rgba(17, 42, 54, 0.46)",
  },
  metricValue: {
    fontFamily: '"Monument Extended", sans-serif',
    fontSize: 18,
    color: "#f1f1f1",
    lineHeight: 1.2,
  },
  metricLabel: {
    marginTop: theme.spacing(0.75),
    fontSize: 12,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "rgba(241, 241, 241, 0.62)",
  },
  formPanel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2.5, 2, 4),
    },
  },
  formCard: {
    width: "100%",
    maxWidth: 460,
    borderRadius: 28,
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(10, 27, 34, 0.86)"
        : "rgba(255, 255, 255, 0.95)",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 30px 80px rgba(0, 0, 0, 0.28)"
        : "0 24px 64px rgba(15, 23, 42, 0.12)",
    padding: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3),
      borderRadius: 22,
    },
  },
  formHeader: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  formKicker: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    color: theme.palette.primary.main,
  },
  formTitle: {
    fontFamily: '"Monument Extended", sans-serif',
    fontSize: 24,
    letterSpacing: "-0.02em",
    color: theme.palette.text.primary,
  },
  formText: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
  field: {
    marginBottom: theme.spacing(1.5),
  },
  inputRoot: {
    borderRadius: "14px !important",
    backgroundColor: `${theme.palette.inputBackground || theme.fluyra.tokens.inputBg} !important`,
    color: `${theme.palette.text.primary} !important`,
    "&:hover $inputNotchedOutline": {
      borderColor: `${theme.palette.primary.main} !important`,
    },
    "&$inputFocused": {
      boxShadow: `0 0 0 3px ${theme.fluyra.tokens.accentSoft} !important`,
    },
    "&$inputFocused $inputNotchedOutline": {
      borderColor: `${theme.palette.primary.main} !important`,
      borderWidth: "1px !important",
    },
    "&$inputFocused $inputAdornment svg": {
      color: `${theme.palette.primary.main} !important`,
    },
  },
  inputFocused: {},
  inputNotchedOutline: {
    borderColor: `${theme.fluyra.tokens.inputBorder} !important`,
  },
  inputAdornment: {
    "& svg": {
      color: theme.palette.text.secondary,
      transition: "color 150ms ease",
    },
  },
  labelRoot: {
    color: `${theme.palette.text.secondary} !important`,
  },
  labelFocused: {
    color: `${theme.palette.primary.main} !important`,
  },
  iconStart: {
    color: theme.palette.text.secondary,
    fontSize: 18,
  },
  iconToggle: {
    color: theme.palette.text.secondary,
  },
  rememberRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(2.5),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  rememberControl: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: theme.palette.primary.main,
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: theme.palette.primary.main,
      opacity: 0.32,
    },
  },
  actionButton: {
    width: "100%",
    minHeight: 48,
    borderRadius: 14,
    fontWeight: 700,
    marginTop: theme.spacing(1),
  },
  secondaryButton: {
    width: "100%",
    minHeight: 48,
    borderRadius: 14,
    fontWeight: 700,
    marginTop: theme.spacing(1.25),
    borderColor: theme.palette.divider,
    color: theme.palette.text.primary,
    background:
      theme.palette.type === "dark"
        ? "rgba(17, 42, 54, 0.72)"
        : "rgba(255, 255, 255, 0.82)",
    "&:hover": {
      background: theme.palette.action.hover,
      borderColor: theme.palette.primary.main,
    },
  },
  forgotLink: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    fontWeight: 600,
    "&:hover": {
      textDecoration: "underline",
    },
  },
  errorText: {
    marginBottom: theme.spacing(1),
  },
  supportButton: {
    position: "fixed",
    right: 20,
    bottom: 20,
    width: 60,
    height: 60,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 18px 40px rgba(0, 0, 0, 0.28)"
        : "0 18px 40px rgba(15, 23, 42, 0.2)",
    zIndex: 5,
    transition: "transform 150ms ease, background 150ms ease",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      transform: "scale(1.06)",
    },
  },
  supportIcon: {
    fontSize: 34,
  },
}));

const Login = () => {
  const classes = useStyles();
  const { handleLogin } = useContext(AuthContext);
  const features = [
    "Atendimento Multi-Canal.",
    "Flow Builder Inteligente.",
    "Agentes de Atendimento com IA.",
  ];

  const [user, setUser] = useState({
    email: "",
    password: "",
    remember: false,
  });
  const [error] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [userCreationEnabled, setUserCreationEnabled] = useState(true);

  useEffect(() => {
    document.body.classList.add("login-page");
    return () => document.body.classList.remove("login-page");
  }, []);

  useEffect(() => {
    try {
      const rememberedLogin = JSON.parse(
        localStorage.getItem(REMEMBERED_LOGIN_KEY) || "null"
      );

      if (rememberedLogin?.remember && rememberedLogin?.email) {
        setUser((currentUser) => ({
          ...currentUser,
          email: rememberedLogin.email,
          remember: true,
        }));
      }
    } catch (_) {
      localStorage.removeItem(REMEMBERED_LOGIN_KEY);
    }
  }, []);

  useEffect(() => {
    const fetchUserCreationStatus = async () => {
      try {
        const { data } = await openApi.get("/settings/userCreation");
        setUserCreationEnabled(data.userCreation === "enabled");
      } catch (err) {
        setUserCreationEnabled(false);
      }
    };

    fetchUserCreationStatus();
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    const lang = localStorage.getItem("i18nextLng") || "pt";
    i18n.changeLanguage(lang);

    if (user.remember && user.email) {
      localStorage.setItem(
        REMEMBERED_LOGIN_KEY,
        JSON.stringify({ email: user.email, remember: true })
      );
    } else {
      localStorage.removeItem(REMEMBERED_LOGIN_KEY);
    }

    handleLogin(user);
  };

  return (
    <>
      <Helmet>
        <title>Login</title>
      </Helmet>

      <div className={classes.root}>
        <section className={classes.brandPanel}>
          <div className={classes.brandGlow} />

          <div className={classes.brandCard}>
            <div className={classes.brandContent}>
              <div className={classes.brandGrid} />
              <div className={`${classes.brandOrbit} ${classes.orbitOne}`} />
              <div className={`${classes.brandOrbit} ${classes.orbitTwo}`} />

              <div className={classes.brandHeader}>
                <span className={classes.brandBadge}>Conversational CRM</span>
                <img src={fluyraLogoDark} alt="Fluyra" className={classes.brandLogo} />
                <Typography component="h1" className={classes.slogan}>
                  Conecte, atenda e fidelize.
                </Typography>
                <Typography className={classes.brandText}>
                  Uma operação moderna para WhatsApp com automação, contexto e velocidade real de atendimento.
                </Typography>
              </div>

              <div>
                <div className={classes.featureList}>
                  {features.map((feature) => (
                    <div key={feature} className={classes.featureItem}>
                      <div className={classes.featureIconWrap}>
                        <CheckCircleOutlineIcon />
                      </div>
                      <Typography className={classes.featureTitle}>
                        {feature}
                      </Typography>
                    </div>
                  ))}
                </div>

                <div className={classes.metricsRow}>
                  <div className={classes.metricCard}>
                    <div className={classes.metricValue}>Omnichannel</div>
                    <div className={classes.metricLabel}>Operação centralizada</div>
                  </div>
                  <div className={classes.metricCard}>
                    <div className={classes.metricValue}>Fluxos IA</div>
                    <div className={classes.metricLabel}>Automação assistida</div>
                  </div>
                  <div className={classes.metricCard}>
                    <div className={classes.metricValue}>Tempo real</div>
                    <div className={classes.metricLabel}>Visão de performance</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={classes.formPanel}>
          <form
            className={classes.formCard}
            onSubmit={handleSubmit}
            autoComplete="on"
            method="post"
          >
            <div className={classes.formHeader}>
              <span className={classes.formKicker}>Acesso seguro</span>
              <Typography className={classes.formTitle}>Entrar na operação</Typography>
              <Typography className={classes.formText}>
                Acesse sua central Fluyra e continue o atendimento de onde parou.
              </Typography>
            </div>

            {error ? (
              <Typography color="error" className={classes.errorText}>
                {error}
              </Typography>
            ) : null}

            <TextField
              id="email"
              name="email"
              label="Email"
              variant="outlined"
              fullWidth
              className={classes.field}
              type="email"
              autoComplete="username"
              inputMode="email"
              value={user.email}
              onChange={(event) => setUser({ ...user, email: event.target.value })}
              inputProps={{
                autoCapitalize: "none",
                autoCorrect: "off",
                spellCheck: "false",
              }}
              InputLabelProps={{
                classes: {
                  root: classes.labelRoot,
                  focused: classes.labelFocused,
                },
              }}
              InputProps={{
                classes: {
                  root: classes.inputRoot,
                  focused: classes.inputFocused,
                  notchedOutline: classes.inputNotchedOutline,
                },
                startAdornment: (
                  <InputAdornment position="start" className={classes.inputAdornment}>
                    <EmailIcon className={classes.iconStart} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              id="password"
              name="password"
              label="Senha"
              variant="outlined"
              fullWidth
              className={classes.field}
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={user.password}
              onChange={(event) => setUser({ ...user, password: event.target.value })}
              InputLabelProps={{
                classes: {
                  root: classes.labelRoot,
                  focused: classes.labelFocused,
                },
              }}
              InputProps={{
                classes: {
                  root: classes.inputRoot,
                  focused: classes.inputFocused,
                  notchedOutline: classes.inputNotchedOutline,
                },
                startAdornment: (
                  <InputAdornment position="start" className={classes.inputAdornment}>
                    <LockIcon className={classes.iconStart} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" className={classes.inputAdornment}>
                    <IconButton
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      className={classes.iconToggle}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <div className={classes.rememberRow}>
              <div className={classes.rememberControl}>
                <Switch
                  checked={user.remember}
                  onChange={(event) => {
                    const remember = event.target.checked;

                    if (!remember) {
                      localStorage.removeItem(REMEMBERED_LOGIN_KEY);
                    }

                    setUser({ ...user, remember });
                  }}
                />
                <Typography variant="body2">Lembrar de mim</Typography>
              </div>

              <RouterLink to="/forgot-password" className={classes.forgotLink}>
                Esqueceu a senha?
              </RouterLink>
            </div>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              className={classes.actionButton}
            >
              Entrar
            </Button>

            {userCreationEnabled ? (
              <Button
                component={RouterLink}
                to="/signup"
                variant="outlined"
                className={classes.secondaryButton}
              >
                Cadastre-se
              </Button>
            ) : null}
          </form>
        </section>

        <div
          className={classes.supportButton}
          onClick={() => window.open("https://wa.me/5500000000000", "_blank")}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              window.open("https://wa.me/5500000000000", "_blank");
            }
          }}
        >
          <WhatsAppIcon className={classes.supportIcon} />
        </div>
      </div>
    </>
  );
};

export default Login;
